import type { CatalogFacet } from './types'

/**
 * The left-hand facet sidebar on `CatalogSeriesPage` (ADR-0120/ADR-0121) —
 * pure logic only, no fetch. Split out of the component/hooks the same way
 * `catalog-series-filters.ts` splits its predicate off
 * `CatalogSeriesFilterSidebar`.
 */

/**
 * The existential live-count facet model (frontend
 * `claude-feature-notes/2026-08-28-existential-live-count-facet-model.md`
 * §1/§3) — supersedes this module's earlier ADR-0121 D3 "series-safe +
 * near-full-coverage" gate (the 90%-of-`safeToFilter` rule this function used
 * to implement). `grains.series.safeToFilter` / `invariantUnits` are NOT
 * deleted — they still exist on the wire and are retained below as an
 * OPTIONAL, secondary disclosure signal (the "✓ exact for N of M" badge), but
 * they no longer decide whether a facet renders at all. Existential
 * ("does the current candidate set still contain this value") is the new
 * gate; universal invariance is a different claim layered on top, per the
 * risk review's false-equivalence warning (ADR-0120 D4) — the two must never
 * be collapsed into one visual treatment.
 */

/** A facet with fewer than this many distinct values partitions nothing —
 * the always-all-or-nothing-checkbox case. Reused verbatim: the wire
 * contract already guarantees `values.length >= 2` (ADR-0121 D4), but this
 * stays an explicit, live check rather than an assumption about upstream. */
export const MIN_FACET_VALUES = 2

/**
 * The zero-selection coverage floor (plan §3): a facet renders on the
 * UNSCOPED (no `specs` selected) view only if it is carried by every series
 * that carries ANY facet-eligible spec at all — 100%, not a percentage
 * threshold. This is what keeps the first-paint sidebar to a small,
 * near-universal set instead of every facet a handful of series happen to
 * carry (`Width`: 1 series; `Keyway Depth`: 2).
 *
 * NOT compared against the wire's `totalUnits` (the raw category series
 * count). Confirmed against the real Linear Shafts corpus: two series
 * ("Linear Guide Assembly - Standard/Wide Carriage", "Precision Linear
 * Shaft - SF Series") carry NO facet-eligible spec value on ANY facet —
 * every facet in the category tops out at `measuredUnits` 145 of a
 * `totalUnits` of 147, so `measuredUnits === totalUnits` is unsatisfiable by
 * construction and silently empties the entire sidebar (confirmed live: this
 * bug shipped and was caught by a real browser check finding zero facets
 * rendered). The correct universe size is the REACHABLE ceiling — the
 * largest `measuredUnits` actually observed across the category's own
 * facets in this response — derived dynamically, never hard-coded, so a
 * category with a different unreachable-series count still self-corrects.
 * This keeps the "strict 100%, no tunable percentage" rule the plan asked
 * for; only the denominator was wrong, not the threshold.
 *
 * `reachableCeiling` is computed once per response by the caller
 * ({@link isSeriesFacetRenderable}'s caller in `useCatalogFacets.ts`) and
 * threaded through rather than recomputed per facet.
 */
function hasFullSeriesCoverage(facet: CatalogFacet, reachableCeiling: number): boolean {
  const { measuredUnits } = facet.grains.series
  if (measuredUnits === null || reachableCeiling <= 0) return false
  return measuredUnits === reachableCeiling
}

/**
 * The reachable ceiling `hasFullSeriesCoverage` compares against — the
 * largest `measuredUnits` observed across a category's own facets, standing
 * in for "how many series in this category can ever carry a facet-eligible
 * spec at all" (see {@link hasFullSeriesCoverage}'s doc comment for why this
 * is NOT the wire's `totalUnits`). `0` (never satisfiable) when the list is
 * empty or every facet is unmeasured.
 */
export function reachableSeriesCeiling(facets: readonly CatalogFacet[]): number {
  let max = 0
  for (const facet of facets) {
    const measured = facet.grains.series.measuredUnits
    if (measured !== null && measured > max) max = measured
  }
  return max
}

/**
 * Whether one facet from `GET /catalog/categories/{category}/facets` is
 * worth a checkbox group on the SERIES-card grid — THE gate `useCatalogFacets`
 * applies, branching on whether any spec is already selected (plan §3):
 *
 * - Zero specs selected (`selection` empty): the response is the
 *   precomputed, query-independent catalogue (no live `valueCounts`), so this
 *   applies {@link hasFullSeriesCoverage} on top of the `MIN_FACET_VALUES`
 *   check — the strict first-paint floor.
 * - >=1 spec selected: the response is already the LIVE drilldown, narrowed
 *   server-side to the candidate set that selection produced — a facet
 *   appearing there at all already means something in that narrowed set
 *   still carries it, so nothing further gates it beyond `MIN_FACET_VALUES`.
 *   No coverage floor in this branch, by design: this is exactly what let
 *   `Inner Diameter` (15/145 series overall — noise unscoped) surface as a
 *   near-universal filter (15/16) once `Shaft Style=Hollow` narrowed the set.
 *
 * `selection` is threaded through (not just a `hasSelection` boolean) for one
 * more, narrower reason: a live-browser bug found selecting ANY facet value
 * (e.g. `Shaft Style: Hollow`) legitimately narrows that SAME facet's own
 * `values` down to just the one(s) still present in the candidate set — often
 * exactly 1, since the values that produced zero remaining matches get
 * dropped server-side. The plain `MIN_FACET_VALUES` check would then filter
 * the ENTIRE group out of the sidebar the instant a shopper checks a box,
 * making their own selection disappear with no way to see it's checked or
 * un-check it (short of "Clear all"). `facet.name in selection` is checked
 * FIRST, before the `MIN_FACET_VALUES` floor, so the facet the shopper is
 * actively filtering by always stays rendered — even down to a single
 * checked value — while an unrelated, near-universal 1-value facet elsewhere
 * in the response still gets filtered out as noise. This is deliberately
 * narrow: it is not a general relaxation of `MIN_FACET_VALUES`, only an
 * exception for the facet that IS the current selection.
 */
export function isSeriesFacetRenderable(
  facet: CatalogFacet,
  selection: Readonly<Record<string, string>>,
  reachableCeiling: number,
): boolean {
  const isCurrentSelection = facet.name in selection
  if (facet.values.length < MIN_FACET_VALUES) return isCurrentSelection
  if (Object.keys(selection).length === 0) return hasFullSeriesCoverage(facet, reachableCeiling)
  return true
}

/**
 * The optional secondary disclosure signal (plan §1) — the retained
 * invariance measurement, surfaced as "exact for N of M" rather than used to
 * decide rendering. Returns `null` when the wire didn't carry a usable
 * measurement (unmeasured, or the facet isn't actually series-safe) rather
 * than a fabricated string (ADR-0090/0091 "omit, don't stub").
 */
export function seriesExactnessDisclosure(facet: CatalogFacet): string | null {
  const { safeToFilter, invariantUnits, measuredUnits } = facet.grains.series
  if (!safeToFilter || invariantUnits === null || measuredUnits === null) return null
  return `exact for ${invariantUnits} of ${measuredUnits}`
}

/**
 * One facet value's live count within the current (possibly narrowed)
 * candidate set (UID-387) — looks up the entry off `facet.valueCounts`
 * matching `value`, or `undefined` when the catalogue carries no live counts
 * at all (the unscoped/zero-selection view — see `CatalogFacet.valueCounts`'s
 * OMITTED-not-empty doc comment on the wire type).
 */
export function facetValueCount(
  facet: CatalogFacet,
  value: string,
): NonNullable<CatalogFacet['valueCounts']>[number] | undefined {
  return facet.valueCounts?.find((entry) => entry.value === value)
}

const SPEC_PARAM = /^specs\[(.+)\]$/

/**
 * Reads the `specs[<name>]=<value>` bracket params off the current URL —
 * the same wire shape `GET /catalog/search` accepts (see
 * `reference_bracketed-query-params-openapi`), reused here as the shareable/
 * bookmarkable URL convention rather than inventing a second one. A
 * blank/whitespace value is treated as absent, mirroring the BFF's own rule
 * for the same param.
 */
export function parseFacetSelection(params: URLSearchParams): Readonly<Record<string, string>> {
  const selection: Record<string, string> = {}
  for (const [key, value] of params.entries()) {
    const match = SPEC_PARAM.exec(key)
    const facetName = match?.[1]
    if (facetName !== undefined && value.trim().length > 0) {
      selection[facetName] = value
    }
  }
  return selection
}

/**
 * Rebuilds `params` with its `specs[*]` entries replaced by `selection` —
 * every OTHER param (e.g. the grid/list `view` toggle, were it ever URL-
 * synced) survives untouched. An empty `selection` clears every `specs[*]`
 * entry, which is exactly "Clear all"'s URL effect.
 */
export function facetSearchParams(
  params: URLSearchParams,
  selection: Readonly<Record<string, string>>,
): URLSearchParams {
  const next = new URLSearchParams(params)
  for (const key of [...next.keys()]) {
    if (SPEC_PARAM.test(key)) next.delete(key)
  }
  for (const [name, value] of Object.entries(selection)) {
    next.set(`specs[${name}]`, value)
  }
  return next
}

/**
 * The "Search filters" input's predicate (client-side only — no backend
 * text-search endpoint exists for the facet catalogue). Matches a facet's own
 * name or any of its selectable values, case-insensitively.
 */
export function matchesFacetSearchText(facet: CatalogFacet, searchText: string): boolean {
  const needle = searchText.trim().toLowerCase()
  if (needle.length === 0) return true
  if (facet.name.toLowerCase().includes(needle)) return true
  return facet.values.some((value) => value.toLowerCase().includes(needle))
}
